import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, UsersThree, Tray, SealCheck, Funnel, Scales, Wrench, ArrowsClockwise, ChatCircleText, ListChecks, ShareNetwork, ChartBar, Eye, Stack, ClockCounterClockwise } from "@phosphor-icons/react";
import { TYPE_META } from "@/components/delivery/destinationConstants";
import { SendTestLeadButton } from "@/components/SendTestLeadButton";
import { TestInboxViewer, fmtWhen } from "@/components/TestInboxViewer";
import { ThankYouSettingsDialog } from "@/components/ThankYouSettingsDialog";
import { BuyerFunnelsDialog } from "@/components/BuyerFunnelsDialog";
import { MatchCheckPanel } from "@/components/MatchCheckPanel";
import { BuyerLegalDialog } from "@/components/legal/BuyerLegalDialog";
import { BuyerRotationDialog, capsLabel } from "@/components/buyer/BuyerRotationDialog";
import { picksLabel } from "@/lib/funnelOptions";
import { TestimonialsDialog } from "@/components/testimonials/TestimonialsDialog";
import { FormRulesDialog } from "@/components/pageSettings/FormRulesDialog";
import { LeadShareDialog } from "@/components/pageSettings/LeadShareDialog";
import { BuyerTestDialog } from "@/components/thankyou/BuyerTestDialog";
import { ThankYouPreviewDialog } from "@/components/thankyou/ThankYouPreviewDialog";
import { BuildPagesDialog } from "@/components/landers/BuildPagesDialog";
import { LoginAsBuyerButton } from "@/components/LoginAsButton";
import { LoginStatusBadge } from "@/components/buyer/LoginStatusBadge";
import { BuyerRefreshButton } from "@/components/buyer/BuyerRefreshButton";
import InviteBuyerModal from "@/components/delivery/InviteBuyerModal";
import { PageRequestsPanel } from "@/components/PageRequestsPanel";
import { BuyerHistoryDialog } from "@/components/buyer/BuyerHistoryDialog";
import { SpreadLayoutsDialog } from "@/components/thankyou/SpreadLayoutsDialog";

function ReviewBadge({ r, code }) {
  if (!r) return null;
  const ok = r.decision === "approved";
  return (
    <span title={r.note || ""} data-testid={`global-buyer-ty-review-${code}`}
      className={`ml-1 border px-1 text-[9px] uppercase tracking-widest ${ok ? "border-emerald-700 bg-emerald-100" : "border-amber-700 bg-amber-100"}`}>
      {ok ? "Thank-you approved" : "Thank-you changes asked"}
    </span>
  );
}

const ROW_BTN = "mt-1.5 border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-black hover:text-white";
const money = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function BuyerSummary({ b }) {
  const p = b.price;
  const priced = Object.keys(b.funnel_prices || {}).length;
  return (
    <div className="mt-1 text-[10.5px] text-zinc-700" data-testid={`global-buyer-picks-${b.code}`}>
      {picksLabel(b.picks)}
      {p && <span className="text-zinc-500"> · {p.min === p.max ? money(p.min) : `${money(p.min)}–${money(p.max)}`}/lead</span>}
      {priced > 0 && <span className="text-zinc-500"> · {priced} funnel price{priced === 1 ? "" : "s"}</span>}
      {b.pending_funnels && (
        <span className="ml-1 border border-amber-700 bg-amber-100 px-1 text-[9px] uppercase tracking-widest" data-testid={`global-buyer-funnels-pending-${b.code}`}>Change requested</span>
      )}
      {b.caps && (
        <div className={b.caps.at_cap ? "text-red-700 font-bold" : "text-zinc-500"} data-testid={`global-buyer-rotation-${b.code}`}>{capsLabel(b.caps, b.rotation_share)}</div>
      )}
    </div>
  );
}

function checkLabel(t) {
  if (!t.total) return { text: "Not delivered", cls: "bg-red-100 border-red-700 text-red-800" };
  if (t.delivered === t.total) return { text: "Delivered", cls: "bg-emerald-100 border-emerald-700 text-emerald-800" };
  if (t.delivered > 0) return { text: `Partly delivered ${t.delivered}/${t.total}`, cls: "bg-amber-100 border-amber-700 text-amber-900" };
  return { text: "Failed", cls: "bg-red-100 border-red-700 text-red-800" };
}

export function DeliveryCheckBadge({ t, code, testId = `global-buyer-check-badge-${code}` }) {
  if (!t) {
    return <span className="border border-zinc-400 text-zinc-500 px-1.5 py-0.5 text-[9px] uppercase tracking-widest" data-testid={testId}>Never tested</span>;
  }
  const { text, cls } = checkLabel(t);
  const tip = (t.results || []).map((r) => `${r.status === "delivered" ? "OK" : "FAIL"} · ${r.destination_name}${r.reason ? ` — ${r.reason}` : ""}`).join("\n");
  return (
    <span className={`border px-1.5 py-0.5 text-[9px] uppercase tracking-widest inline-flex items-center gap-1 ${cls}`} title={tip}
          data-testid={testId}>
      {text} · {fmtWhen(t.at)}
    </span>
  );
}

function BuyerRow({ b, onChanged }) {
  const d = b.delivery;
  const [inboxOpen, setInboxOpen] = useState(false);
  const [tyOpen, setTyOpen] = useState(false);
  const [funnelsOpen, setFunnelsOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [rotationOpen, setRotationOpen] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [tyTestOpen, setTyTestOpen] = useState(false);
  const [tyPreviewOpen, setTyPreviewOpen] = useState(false);
  const [buildOpen, setBuildOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const navigate = useNavigate();
  const finish = () => (b.org_id ? navigate(`/clients/${b.org_id}/setup`)
    : navigate("/clients/setup", { state: { draft: { id: b.id, firm_name: b.firm_name, legal_name: b.legal_name } } }));
  const [inviting, setInviting] = useState(false);
  return (
    <>
      <tr className="block sm:table-row border-b-2 sm:border-b border-black sm:border-zinc-200 align-top" data-testid={`global-buyer-row-${b.code}`}>
        <td className="block sm:table-cell px-3 py-2">
          <div className="font-bold flex items-center gap-1.5">
            {b.firm_name}
            {b.is_test && <span className="border border-black bg-amber-200 px-1 text-[9px] uppercase tracking-widest" data-testid={`global-buyer-test-chip-${b.code}`}>Test</span>}
            {b.is_draft && <span className="border border-black bg-zinc-200 px-1 text-[9px] uppercase tracking-widest" data-testid={`global-buyer-draft-chip-${b.code}`}>Draft</span>}
            <LoginStatusBadge login={b.login} testId={`global-buyer-login-status-${b.code}`}
                              onInvite={b.destination_id && b.delivery ? () => setInviting(true) : undefined} />
          </div>
          {b.legal_name && <div className="text-[10px] text-zinc-600" data-testid={`global-buyer-legal-name-${b.code}`}>{b.legal_name}</div>}
          <div className="text-[10px] font-mono text-zinc-500">?b={b.code} · {b.status}{b.lander_count > 0 && ` · ${b.lander_count} own page${b.lander_count === 1 ? "" : "s"}`}<ReviewBadge r={b.ty_review} code={b.code} /></div>
          {b.call_taps && (
            <div className="text-[10px] font-mono text-zinc-500" data-testid={`global-buyer-call-taps-${b.code}`}
                 title="Call button taps in the last 30 days">
              Call taps 30d: {b.call_taps.landing} landing · {b.call_taps.thank_you} thank-you
            </div>
          )}
          <BuyerSummary b={b} />
          {b.ty_design && (
            <div className="text-[10px] font-mono text-zinc-500" data-testid={`global-buyer-ty-layout-${b.code}`}>
              Thank-you layout: {b.ty_design.name}{b.ty_design.is_default ? " (site default)" : ""}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {(b.is_draft || (b.org_id && b.status !== "published")) && (
              <button type="button" onClick={finish} data-testid={`global-buyer-finish-setup-${b.code}`} className={`${ROW_BTN.replace("bg-white", "bg-black")} text-white hover:bg-zinc-800`}>
                <Wrench size={11} weight="bold" /> Finish setup
              </button>
            )}
            <button type="button" onClick={() => setBuildOpen(true)} data-testid={`global-buyer-build-pages-${b.code}`} className={ROW_BTN}>
              <Stack size={11} weight="bold" /> Build all pages
            </button>
            <button type="button" onClick={() => setFunnelsOpen(true)} data-testid={`global-buyer-funnels-${b.code}`} className={ROW_BTN}>
              <Funnel size={11} weight="bold" /> Pages &amp; price
            </button>
            {b.destination_id && <LoginAsBuyerButton url={`/admin/buyers/${b.id}/logins`} testId={`global-buyer-login-as-${b.code}`} className="mt-1.5 self-start" />}
            <button type="button" onClick={() => setTyOpen(true)} data-testid={`global-buyer-ty-${b.code}`} className={ROW_BTN}>
              <SealCheck size={11} weight="bold" /> Thank-you page
            </button>
            <button type="button" onClick={() => setTyPreviewOpen(true)} data-testid={`global-buyer-ty-preview-${b.code}`} className={ROW_BTN}>
              <Eye size={11} weight="bold" /> Preview thank-you page
            </button>
            <button type="button" onClick={() => setLegalOpen(true)} data-testid={`global-buyer-legal-${b.code}`} className={ROW_BTN}>
              <Scales size={11} weight="bold" /> Legal
            </button>
            <button type="button" onClick={() => setRotationOpen(true)} data-testid={`global-buyer-rotation-btn-${b.code}`} className={ROW_BTN}>
              <ArrowsClockwise size={11} weight="bold" /> Rotation &amp; caps
            </button>
            <button type="button" onClick={() => setReviewsOpen(true)} data-testid={`global-buyer-testimonials-${b.code}`} className={ROW_BTN}>
              <ChatCircleText size={11} weight="bold" /> Testimonials
            </button>
            <button type="button" onClick={() => setRulesOpen(true)} data-testid={`global-buyer-form-rules-${b.code}`} className={ROW_BTN}>
              <ListChecks size={11} weight="bold" /> Form questions
            </button>
            {b.destination_id && b.delivery && (
              <button type="button" onClick={() => setShareOpen(true)} data-testid={`global-buyer-lead-fields-${b.code}`} className={ROW_BTN}>
                <ShareNetwork size={11} weight="bold" /> Lead fields sent
              </button>
            )}
            <button type="button" onClick={() => setTyTestOpen(true)} data-testid={`global-buyer-ty-test-${b.code}`} className={ROW_BTN}>
              <ChartBar size={11} weight="bold" /> Design test
            </button>
            <button type="button" onClick={() => setHistoryOpen(true)} data-testid={`global-buyer-history-${b.code}`} className={ROW_BTN}>
              <ClockCounterClockwise size={11} weight="bold" /> History
            </button>
          </div>
          {historyOpen && <BuyerHistoryDialog pageId={b.id} firm={b.firm_name} onClose={() => setHistoryOpen(false)} />}
          {tyTestOpen && <BuyerTestDialog pageId={b.id} firm={b.firm_name} onClose={() => setTyTestOpen(false)} />}
          {rulesOpen && <FormRulesDialog scope="page" scopeKey={b.id} title={`${b.firm_name} — form questions`} onClose={() => setRulesOpen(false)} />}
          {shareOpen && <LeadShareDialog destId={b.destination_id} title={`${b.firm_name} — lead fields sent`} onClose={() => setShareOpen(false)} />}
          {reviewsOpen && <TestimonialsDialog pageId={b.id} firm={b.firm_name} onClose={() => setReviewsOpen(false)} />}
          {rotationOpen && <BuyerRotationDialog b={b} onClose={() => setRotationOpen(false)} onSaved={onChanged} />}
          {legalOpen && <BuyerLegalDialog pageId={b.id} onClose={() => setLegalOpen(false)} onSaved={onChanged} />}
          {funnelsOpen && <BuyerFunnelsDialog pageId={b.id} onClose={() => setFunnelsOpen(false)} onSaved={onChanged} />}
          {tyOpen && <ThankYouSettingsDialog pageId={b.id} onClose={() => { setTyOpen(false); onChanged?.(); }} />}
          {tyPreviewOpen && <ThankYouPreviewDialog b={b} onClose={() => setTyPreviewOpen(false)} onSaved={onChanged} />}
          {buildOpen && <BuildPagesDialog b={b} onClose={() => setBuildOpen(false)} onBuilt={onChanged} />}
          {inviting && (
            <InviteBuyerModal destination={{ id: b.destination_id, name: `${b.firm_name} · ${b.delivery?.name || "their delivery"}` }} linkCompany
                              onClose={() => { setInviting(false); onChanged?.(); }} />
          )}
        </td>
        <td className="block sm:table-cell px-3 py-2 text-[11px]" data-testid={`global-buyer-delivery-${b.code}`}>
          <span className="label-mono sm:hidden">Delivery · </span>
          {d ? <>{TYPE_META[d.type]?.label || d.type}{d.is_active === false && <span className="text-red-700"> · paused</span>}</> : <span className="text-red-700">No delivery setup</span>}
          {d && <div className="mt-1"><DeliveryCheckBadge t={b.last_test} code={b.code} /></div>}
        </td>
        <td className="block sm:table-cell px-3 py-2">
          {d ? (
            <div className="flex flex-col gap-1.5">
              <SendTestLeadButton url="/admin/global-funnels/test-lead" body={{ buyer_page_id: b.id }} testId={`global-buyer-test-lead-${b.code}`} showLeadLink
                                  onSent={onChanged} funnels={b.picks} states={b.states}
                                  confirmText={b.is_test ? undefined : `This sends one lead marked TEST into ${b.firm_name}'s intake system. Continue?`} />
              {b.has_test_inbox && (
                <button type="button" onClick={() => setInboxOpen((o) => !o)} data-testid={`global-buyer-inbox-toggle-${b.code}`}
                        className="self-start border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-black hover:text-white">
                  <Tray size={11} weight="bold" /> {inboxOpen ? "Hide test inbox" : "Open test inbox"}
                </button>
              )}
            </div>
          ) : <span className="text-[10px] text-zinc-500" data-testid={`global-buyer-test-lead-needs-delivery-${b.code}`}>Add a delivery to send test leads</span>}
        </td>
      </tr>
      {inboxOpen && (
        <tr className="block sm:table-row border-b border-zinc-200">
          <td colSpan={3} className="block sm:table-cell px-3 pb-3"><TestInboxViewer pageId={b.id} code={b.code} refreshKey={b.last_test?.at} /></td>
        </tr>
      )}
    </>
  );
}

export const GlobalBuyersPanel = ({ buyers = [], onChanged }) => {
  const navigate = useNavigate();
  const [spreading, setSpreading] = useState(false);
  const rows = buyers.filter((b) => !b.is_sample_page);
  const th = "text-left px-3 py-2 text-[10px] uppercase tracking-widest";
  return (
    <div className="px-4 pb-4" data-testid="global-buyers-panel">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <UsersThree size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter text-sm">Buyers on the shared funnels</div>
        <div className="label-mono text-zinc-600">{rows.length} buyer{rows.length === 1 ? "" : "s"}</div>
        <BuyerRefreshButton onDone={onChanged} className="ml-auto" />
        <button type="button" onClick={() => setSpreading(true)} data-testid="global-buyers-spread-layouts"
                className="border-2 border-black bg-white px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-black hover:text-white transition-colors duration-100">
          <SealCheck size={11} weight="bold" /> Mix up thank-you layouts
        </button>
        <button type="button" onClick={() => navigate("/clients/setup")} data-testid="global-buyers-add-btn"
                className="border-2 border-black bg-black text-white px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-white hover:text-black">
          <Plus size={11} weight="bold" /> Add buyer
        </button>
      </div>
      <PageRequestsPanel onChanged={onChanged} />
      <MatchCheckPanel />
      <div className="overflow-x-auto border-2 border-black bg-white">
        <table className="block sm:table w-full text-xs sm:min-w-[560px]" data-testid="global-buyers-table">
          <thead className="hidden sm:table-header-group bg-zinc-100 border-b-2 border-black">
            <tr><th className={th}>Buyer</th><th className={th}>Delivery · last check</th><th className={th}>Test lead</th></tr>
          </thead>
          <tbody className="block sm:table-row-group">
            {rows.map((b) => <BuyerRow key={b.id} b={b} onChanged={onChanged} />)}
            {!rows.length && (
              <tr><td colSpan={3} className="px-3 py-6 text-center label-mono" data-testid="global-buyers-empty">No buyers yet — add your first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {spreading && <SpreadLayoutsDialog onClose={() => setSpreading(false)} onDone={onChanged} />}
    </div>
  );
};
